import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LoroDoc } from "loro-crdt";
import { Button } from "../ui";
import { archiveTask, setProp, unarchiveTask } from "../core/ops";
import { notBeforeMs } from "../core/notYet";
import type { Task } from "../core/schema";
import { useToasts } from "../ui/toasts";
import { formatNotBefore } from "./earlyDone";
import { SOMEDAY_EVERY_DAYS, readReviewAt, reviewCandidates, reviewDue, writeReviewAt } from "./notNowLogic";

/**
 * Once a week, *Look at what's set aside?* (UI cleanup U4): one line until the person chooses to look, then each
 * task with four choices. Someday tasks join every four weeks. A row leaves only through the person's own choice.
 */
export function AsideReview({
  tasks,
  nowMs,
  write,
  openTask,
}: {
  tasks: Task[];
  nowMs: number;
  write(fn: (doc: LoroDoc) => void): void;
  openTask(id: string): void;
}) {
  const { t, i18n } = useTranslation();
  const pushToast = useToasts((s) => s.push);
  const [last, setLast] = useState(() => ({
    aside: readReviewAt("aside"),
    someday: readReviewAt("someday"),
  }));
  const [looking, setLooking] = useState(false);
  const [handled, setHandled] = useState<Set<string>>(new Set());
  // what to look at is fixed when the look starts, so rows never re-sort under the pointer (§9.19)
  const [frozen, setFrozen] = useState<ReturnType<typeof reviewCandidates> | null>(null);
  const live = useMemo(
    () => reviewCandidates(tasks, nowMs, last, (task, now) => notBeforeMs(task, now)),
    [tasks, nowMs, last],
  );
  const shown = frozen ?? live;
  const n = shown.aside.length + shown.someday.length;
  if (n === 0) return null;

  const finish = () => {
    const at = Date.now();
    // a due someday look counts as done too, also when it had nothing in it: a task made someday during this
    // look must not bring the question straight back
    const someday = reviewDue(last.someday, SOMEDAY_EVERY_DAYS, at) ? at : last.someday;
    writeReviewAt("aside", at);
    if (someday !== last.someday) writeReviewAt("someday", at);
    setLast({ aside: at, someday });
    setLooking(false);
    setFrozen(null);
    setHandled(new Set());
  };
  const done = (id: string) => setHandled((s) => new Set(s).add(id));
  const letGo = (task: Task) => {
    write((doc) => archiveTask(doc, task.id));
    pushToast(t("task.archivedToast", { title: task.title }), {
      action: { label: t("common.undo"), run: () => write((doc) => unarchiveTask(doc, task.id)) },
    });
    done(task.id);
  };
  const choice = (label: string, testId: string, run: () => void) => (
    <Button variant="ghost" size="sm" onClick={run} data-testid={testId}>
      {label}
    </Button>
  );
  const row = (task: Task, sub: string, choices: React.ReactNode) =>
    !handled.has(task.id) && (
      <li
        key={task.id}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2"
        data-testid="aside-row"
      >
        <button
          type="button"
          className="min-w-0 flex-1 basis-40 truncate text-left text-sm hover:text-accent"
          onClick={() => openTask(task.id)}
        >
          {task.title}
        </button>
        <span className="text-[12px] text-neutral-500">{sub}</span>
        <span className="flex flex-wrap gap-1">{choices}</span>
      </li>
    );

  return (
    <section className="mt-6" data-testid="aside-review">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <span className="flex-1 text-sm">
          {t("aside.title")} <span className="text-neutral-500">{t("aside.count", { n })}</span>
        </span>
        {looking ? (
          <Button size="sm" variant="secondary" onClick={finish} data-testid="aside-done">
            {t("aside.done")}
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setFrozen(live);
                setLooking(true);
              }}
              data-testid="aside-look"
            >
              {t("aside.look")}
            </Button>
            <Button size="sm" variant="ghost" onClick={finish} data-testid="aside-not-this-week">
              {t("aside.notThisWeek")}
            </Button>
          </>
        )}
      </div>
      {looking && (
        <ul className="kss-surface mt-2 divide-y divide-neutral-200 dark:divide-neutral-800">
          {shown.aside.map(({ task, back }) =>
            row(
              task,
              t("aside.backFrom", { when: formatNotBefore(back, i18n.language) }),
              <>
                {choice(t("aside.backNow"), "aside-back-now", () => {
                  write((doc) => setProp(doc, task.id, "startAt", null));
                  done(task.id);
                })}
                {choice(t("aside.keep"), "aside-keep", () => done(task.id))}
                {choice(t("aside.someday"), "aside-someday", () => {
                  write((doc) => {
                    setProp(doc, task.id, "horizon", "someday");
                    setProp(doc, task.id, "startAt", null);
                  });
                  done(task.id);
                })}
                {choice(t("aside.letGo"), "aside-let-go", () => letGo(task))}
              </>,
            ),
          )}
          {shown.someday.map((task) =>
            row(
              task,
              t("aside.isSomeday"),
              <>
                {choice(t("aside.backNow"), "aside-back-now", () => {
                  write((doc) => setProp(doc, task.id, "horizon", "now"));
                  done(task.id);
                })}
                {choice(t("aside.keep"), "aside-keep", () => done(task.id))}
                {choice(t("aside.letGo"), "aside-let-go", () => letGo(task))}
              </>,
            ),
          )}
        </ul>
      )}
    </section>
  );
}
